import React, {useState, useEffect} from 'react';
import {Box, Typography, FormControlLabel} from '@mui/material';
import ArrButton from '../../common/arr-button/ArrButton';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import ArrCheckbox from '../../common/arr-checkbox/ArrCheckbox';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import './BulkReleaseFlyout.scss';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {useProjectSecurityGroups} from '../../../hooks/users/useUsers';
import {t} from 'i18next';

export interface SecurityGroup {
  id: string;
  name: string;
  description?: string;
}

export interface SelectedSecurityGroup {
  id: string;
  value?: string;
}

export interface BulkReleaseFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
  onRelease: (selectedIds: string[]) => void;
  onUnrelease: (selectedIds: string[]) => void;
  title?: string;
  size?: 'large';
  loading?: boolean;
}

const BulkReleaseFlyout: React.FC<BulkReleaseFlyoutProps> = ({
  isOpen = false,
  onClose,
  onRelease,
  onUnrelease,
  title = 'Release Docs to/Unrelease Docs From Security Group',
  size = 'large',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  loading = false,
}: BulkReleaseFlyoutProps) => {
  const [selectedGroups, setSelectedGroups] = useState<SelectedSecurityGroup[]>(
    []
  );
  const [isProcessing] = useState(false);
  const [allowClose, setAllowClose] = useState(false);
  const [projectId, setProjectId] = useState<number>();
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  useEffect(() => {
    if (loginUserData) {
      setProjectId(loginUserData?.projectId);
    }
  }, [loginUserData]);

  const {data: securityGroups = [], isLoading: isLoadingGroups} =
    useProjectSecurityGroups(
      Number(projectId),
      isOpen && projectId != null && Number.isFinite(Number(projectId))
    );

  useEffect(() => {
    if (isOpen) {
      setAllowClose(false);
    }
  }, [isOpen]);

  const handleGroupToggle = (group: {key: number; value: string}) => {
    setSelectedGroups((prev) => {
      const groupId = String(group.key);
      const isSelected = prev.some((item) => item.id === groupId);
      if (isSelected) {
        return prev.filter((item) => item.id !== groupId);
      } else {
        return [...prev, {id: groupId, value: group.value}];
      }
    });
  };

  const handleRelease = async () => {
    if (selectedGroups.length === 0) return;

    const ids = selectedGroups.map((group) => group.id);
    onRelease(ids);
  };

  const handleUnrelease = async () => {
    if (selectedGroups.length === 0) return;

    const ids = selectedGroups.map((group) => group.id);
    onUnrelease(ids);
    setSelectedGroups([]);
    setAllowClose(true);
    onClose();
  };

  const handleCancel = () => {
    setSelectedGroups([]);
    setAllowClose(true);
    onClose();
  };

  const handleDrawerClose = () => {
    if (allowClose) {
      setAllowClose(false);
      onClose();
    }
  };

  return (
    <ArrDrawer
      isOpen={isOpen}
      onClose={handleDrawerClose}
      size={size}
      title=""
      side="right"
      showButton={false}
      showHeader={false}
      className="bulk-release-flyout"
      ariaLabel="Bulk Release Flyout"
    >
      <Box
        className="bulk-release-flyout__header"
        data-testid="bulk-release-header"
      >
        <Typography
          variant="h6"
          className="typo-text headline3_size title-text-color bulk-release-flyout__title"
          tabIndex={0}
          data-testid="bulk-release-flyout-title"
        >
          {title}
        </Typography>
        <Box
          className="bulk-release-flyout__actions"
          data-testid="bulk-release-actions"
        >
          <ArrButton
            variant="secondary"
            size="small"
            onClick={handleCancel}
            disabled={isProcessing}
            dataTestId="bulk-release-cancel-button"
            aria-label="Cancel and close bulk release panel"
          >
            {t('actionMenu.bulkReleaseFlyout.cancel')}
          </ArrButton>
          <ArrButton
            className={
              selectedGroups.length === 0 || isProcessing ? 'btn-disabled' : ''
            }
            variant="primary"
            size="small"
            onClick={handleUnrelease}
            isAriaDisabled={selectedGroups.length === 0 || isProcessing}
            loading={isProcessing}
            dataTestId="bulk-release-unrelease-button"
            aria-label="Unrelease"
            tabIndex={0}
          >
            {t('actionMenu.bulkReleaseFlyout.unrelease')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="small"
            onClick={handleRelease}
            className={
              selectedGroups.length === 0 || isProcessing ? 'btn-disabled' : ''
            }
            isAriaDisabled={selectedGroups.length === 0 || isProcessing}
            loading={isProcessing}
            dataTestId="bulk-release-release-button"
            aria-label="Release"
            tabIndex={0}
          >
            {t('actionMenu.bulkReleaseFlyout.release')}
          </ArrButton>
        </Box>
      </Box>
      <Box
        className="bulk-release-flyout__content"
        data-testid="bulk-release-content"
        tabIndex={0}
      >
        <Typography
          variant="body2"
          className="typo-text subhead4_size bulk-release-flyout__label"
          data-testid="bulk-release-label"
        >
          {t('actionMenu.bulkReleaseFlyout.selectSecurityGroup')}
        </Typography>

        {isLoadingGroups ? (
          <Box
            className="bulk-release-flyout__loading"
            data-testid="bulk-release-loading"
          >
            <ArrLoadingSpinner aria-label="Loading security groups" />
          </Box>
        ) : (
          <Box
            className="bulk-release-flyout__groups"
            data-testid="bulk-release-groups"
            aria-labelledby="security-groups-label"
          >
            {securityGroups.map((group) => (
              <FormControlLabel
              data-testid={`bulk-release-group-${group.key}`}
                key={group.key}
                control={
                  <ArrCheckbox
                    checked={selectedGroups.some(
                      (item) => item.id === String(group.key)
                    )}
                    onChange={() => handleGroupToggle(group)}
                    disabled={isProcessing}
                    data-testid={`bulk-release-checkbox-${group.key}`}
                    size="small"
                    aria-label={`Select ${group.value} security group`}
                    tabIndex={-1}
                  />
                }
                label={group.value}
                className="bulk-release-flyout__group-item"
              />
            ))}
          </Box>
        )}
      </Box>
    </ArrDrawer>
  );
};

export default BulkReleaseFlyout;
